import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs/internal/Subscription';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataType } from 'src/app/components/data-type-widgets/data-type/data-type-component';
import { AutoWithdrawalTierDTO, avsResultValues } from '../auto-withdrawals-config.model';
import { FieldConfigDTO } from '../../../../../system/system-dtos';
import { AutoWithdrawalsConfigService } from '../auto-withdrawals-config.service';
import { AutoWithdrawalsConfigComponent } from '../auto-withdrawals-config.component';
import { CurrencyService } from '../../../../../common/services/currency.service';

@Component({
  selector: 'app-auto-withdrawals-tier',
  templateUrl: './auto-withdrawals-tier.component.html',
  styleUrls: ['./auto-withdrawals-tier.component.scss'],
  standalone: false,
})
export class AutoWithdrawalsTierComponent implements OnInit, OnDestroy {
  formGroup: UntypedFormGroup;

  tier: AutoWithdrawalTierDTO;

  avsResultValues = avsResultValues;

  subscription: Subscription;

  isFormLoading: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<AutoWithdrawalsConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { tierId: number; priority: number },
    private service: AutoWithdrawalsConfigService,
    private currencyService: CurrencyService
  ) {}

  moneyConfig(name: string): FieldConfigDTO {
    return {
      label: null,
      type: DataType.money,
      name,
      moneyFormat: 'fullValue',
    };
  }

  numberConfig(name: string): FieldConfigDTO {
    return {
      label: null,
      type: DataType.number,
      name,
    };
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    if (this.data?.tierId) {
      this.isFormLoading = true;
      this.service.getTier(this.data?.tierId).subscribe((e) => {
        this.tier = e;
        this.buildForm();
        this.addAvsValues();
        this.setSubscription();
        this.isFormLoading = false;
      });
    } else {
      this.isFormLoading = false;
      this.buildForm();
      this.setSubscription();
    }
  }

  addAvsValues() {
    const setUsedAVSValues = this.avsResultValues.map((item) =>
      (this.tier?.avsResultValues as string[]).includes(item.value) ? item.value : false
    );
    this.formGroup.patchValue({
      avsResultValues: setUsedAVSValues,
    });
  }

  setSubscription() {
    const checkboxControl = this.formGroup.controls.avsResultValues as UntypedFormArray;
    this.subscription = checkboxControl.valueChanges.subscribe(() => {
      checkboxControl.setValue(
        checkboxControl.value.map((value, i) => (value ? this.avsResultValues[i].value : false)),
        { emitEvent: false }
      );
    });
  }

  private buildForm(): void {
    const defaultMoney = {
      value: 0,
      currency: this.currencyService.getCurrencyForCurrentCohort(),
    };
    this.formGroup = this.fb.group({
      id: [this.tier?.id || null],
      active: [this.tier?.active || false],
      priority: [this.tier?.priority || this.data?.priority || 0],
      amountActive: [this.tier?.amountActive || false],
      minAmount: [this.tier?.minAmount || defaultMoney],
      maxAmount: [this.tier?.maxAmount || defaultMoney],
      amountPerDayActive: [this.tier?.amountPerDayActive || false],
      minAmountPerDay: [this.tier?.minAmountPerDay || defaultMoney],
      maxAmountPerDay: [this.tier?.maxAmountPerDay || defaultMoney],
      lifetimeAmountActive: [this.tier?.lifetimeAmountActive || false],
      minLifetimeAmount: [this.tier?.minLifetimeAmount || defaultMoney],
      maxLifetimeAmount: [this.tier?.maxLifetimeAmount || defaultMoney],
      cardsNumberActive: [this.tier?.cardsNumberActive || false],
      cardsNumberMin: [this.tier?.cardsNumberMin || 0],
      cardsNumberMax: [this.tier?.cardsNumberMax || 0],
      totalCardsNumberActive: [this.tier?.totalCardsNumberActive || false],
      totalCardsNumberMin: [this.tier?.totalCardsNumberMin || 0],
      totalCardsNumberMax: [this.tier?.totalCardsNumberMax || 0],
      acceptedForCardActive: [this.tier?.acceptedForCardActive || false],
      cardAgeActive: [this.tier?.cardAgeActive || false],
      minCardAgeDays: [this.tier?.minCardAgeDays || 0],
      cardDocumentVerified: [this.tier?.cardDocumentVerified || false],
      riskScoreActive: [this.tier?.riskScoreActive || false],
      maxRiskScore: [this.tier?.maxRiskScore || 0],
      avsResultActive: [this.tier?.riskScoreActive || false],
      avsResultValues: this.fb.array(this.avsResultValues.map(() => false)),
      minAcceptedForCard: [this.tier?.minAcceptedForCard || 0],
      maxAcceptedForCard: [this.tier?.maxAcceptedForCard || 0],
      requestsPerDayActive: [this.tier?.requestsPerDayActive || false],
      minRequestsPerDay: [this.tier?.minRequestsPerDay || 0],
      maxRequestsPerDay: [this.tier?.maxRequestsPerDay || 0],
      lifetimeRequestsActive: [this.tier?.lifetimeRequestsActive || false],
      minLifetimeRequests: [this.tier?.minLifetimeRequests || 0],
      maxLifetimeRequests: [this.tier?.maxLifetimeRequests || 0],
      bonusAbuseA: [this.tier?.bonusAbuseA || false],
      bonusAbuseB: [this.tier?.bonusAbuseB || false],
      bonusAbuseC: [this.tier?.bonusAbuseC || false],
      withdrawWatch: [this.tier?.withdrawWatch || false],
      hvp: [this.tier?.hvp || false],
      allowOnDataMismatch: [this.tier?.allowOnDataMismatch || false],
      withdrawLteDepositsForPaymentMethod: [this.tier?.withdrawLteDepositsForPaymentMethod || false],
      minCashStakesToDepositsRatioActive: [this.tier?.minCashStakesToDepositsRatioActive || false],
      minCashStakesToDepositsRatio: [this.tier?.minCashStakesToDepositsRatio || 0],
      trustedCard: [this.tier?.trustedCard || false],
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    const checkboxControl = this.formGroup.controls.avsResultValues as UntypedFormArray;
    const formValue = {
      ...this.formGroup.value,
      avsResultValues: checkboxControl.value.filter((value) => !!value),
      avsResultActive: checkboxControl.value.some((value) => !!value),
    };
    if (this.data?.tierId) {
      this.service.updateTier(this.data?.tierId, formValue).subscribe(() => {
        this.dialogRef.close(true);
      });
    } else {
      this.service.insertTier(formValue).subscribe(() => {
        this.dialogRef.close(true);
      });
    }
  }
}
